/**
 * `studentShard-{i}` document schema for the v2 section signup
 * data model.
 *
 * `SECTION_SIGNUP_V2_STUDENT_SHARD_COUNT` shard docs exist per
 * course at `course/{courseId}/sectionSignupsV2/studentShard-{i}`.
 * Each shard holds per-slot student count contributions from
 * students in its hash bucket; summing across all shards gives
 * the course-wide student count for each slot.
 *
 * **Counts, not per-user entries.** Storing `Record<userId, slot>`
 * on a publicly-readable shard would leak every student's pick to
 * every other student. Counts contain no personal data, are
 * freely readable, and the payload per shard is a small map
 * (~80 int entries max per course). Per-user picks still live in
 * each student's own onboarding doc, protected by the existing
 * user-doc security rules.
 *
 * Written by the `setSectionTime` callable, in the same transaction
 * as the student's pick, and rebuilt from the onboarding docs by the
 * `initSectionSignupV2` admin reseed.
 */
import {z} from 'zod';

/**
 * The full student shard document.
 *
 * `counts` is keyed by the `${type}_${timeIndex}` slot key (see
 * `buildSectionSignupV2SlotKey` in the paths module). Values are
 * non-negative integers; the callable increments and decrements
 * via `FieldValue.increment`, which is conflict-free.
 *
 * `.loose()` so IO-managed fields like `updatedAt` pass through
 * on read without appearing in the inferred type.
 */
export const SectionSignupV2StudentShardSchema = z
  .object({
    courseId: z.string(),
    shardIndex: z.number().int().nonnegative(),
    /**
     * Per-slot student count contributed by this shard, keyed by
     * `${type}_${timeIndex}`. Summing across all shards gives the
     * course-wide student count for each slot.
     */
    counts: z.record(z.string(), z.number().int().nonnegative()),
  })
  .loose();

export type SectionSignupV2StudentShard = z.infer<
  typeof SectionSignupV2StudentShardSchema
>;

/**
 * The shard envelope with `counts` values left unchecked, so one bad
 * count can be clamped on its own instead of failing the whole shard.
 */
const SectionSignupV2StudentShardEnvelopeSchema = z
  .object({
    courseId: z.string(),
    shardIndex: z.number().int().nonnegative(),
    counts: z.record(z.string(), z.unknown()),
  })
  .loose();

/** Result of {@link parseSectionSignupV2StudentShard}. */
export interface SectionSignupV2StudentShardParseResult {
  /**
   * The shard with every bad count clamped to 0, or `null` when the
   * envelope itself (`courseId`, `shardIndex`, `counts` map) is malformed.
   */
  shard: SectionSignupV2StudentShard | null;
  /**
   * Slot keys whose stored count was not a non-negative integer and was
   * clamped to 0, for the caller to log. Empty when every count was valid.
   */
  badKeys: string[];
}

/**
 * Parses one stored student shard, validating each count on its own. A
 * negative, fractional, or non-numeric count is clamped to 0 and its key
 * reported, and every other count is kept. A lost or doubled increment can
 * leave one key negative, and dropping the whole shard for it would hide
 * every other student in the shard from availability.
 *
 * @param data - The raw shard document data.
 * @return The clamped shard and the keys that were clamped.
 * @throws Never.
 */
export function parseSectionSignupV2StudentShard(
  data: unknown,
): SectionSignupV2StudentShardParseResult {
  const envelope = SectionSignupV2StudentShardEnvelopeSchema.safeParse(data);
  if (!envelope.success) {
    return {shard: null, badKeys: []};
  }
  const counts: Record<string, number> = {};
  const badKeys: string[] = [];
  for (const [slotKey, count] of Object.entries(envelope.data.counts)) {
    if (typeof count === 'number' && Number.isInteger(count) && count >= 0) {
      counts[slotKey] = count;
      continue;
    }
    counts[slotKey] = 0;
    badKeys.push(slotKey);
  }
  return {shard: {...envelope.data, counts}, badKeys};
}
